import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Button } from '../ui/button'
import { Loader2, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react'
import getCroppedImg from '../../lib/cropImage'
import { bindUploadSignal } from '../../lib/uploadCancellation'
import { applyResize } from '../../lib/cropGeometry'
import { AssetSpecsHint } from './AssetSpecsHint'

// Universal crop-and-resize modal for every image upload across the apps.
// The operator picks one of the preset ratios — 16:9 (horizontal), 9:16
// (vertical), 1:1 (square), or "Maintain" (no crop) — or, when a host needs a
// non-standard ratio (e.g. a 16:13.5 banner), that exact ratio is offered as
// a "Custom" chip and selected by default. Returns the cropped File via
// onConfirm; the backend then generates the remaining variants from it.
//
// This replaces the older `ImageCropModal` (Free / Square / Rectangle) — it is
// API-compatible with it: `defaultRatio` still accepts a raw number or null, so
// existing call sites keep working unchanged.

export type MediaRatio = '16:9' | '9:16' | '1:1' | 'maintain' | 'free'

type RatioOption = { key: string; labelKey: string; fallback: string; value: number | null; values?: Record<string, string> }
type Rect = { x: number; y: number; w: number; h: number }
type Handle = 'body' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

// The four standard presets every host gets. A "Custom" option is prepended
// at runtime when the host passes a non-preset numeric ratio.
const PRESETS: RatioOption[] = [
  { key: '16:9',     labelKey: 'mediaCrop.ratio16x9', fallback: '16:9 — Horizontal', value: 16 / 9 },
  { key: '9:16',     labelKey: 'mediaCrop.ratio9x16', fallback: '9:16 — Vertical',   value: 9 / 16 },
  { key: '1:1',      labelKey: 'mediaCrop.ratio1x1',  fallback: '1:1 — Square',      value: 1 },
  { key: 'maintain', labelKey: 'mediaCrop.maintain',  fallback: 'Maintain',          value: null },
  { key: 'free', labelKey: 'mediaCrop.free', fallback: 'Free crop', value: null },
]

// localStorage key for "last ratio the operator picked". Persisted so an
// operator who runs vertical-only screens doesn't have to switch the chip
// every time. Only ever stores a preset key, and is only consulted when the
// host doesn't pass an explicit defaultRatio.
const RATIO_PREF_KEY = 'meno_media_crop_default_ratio'

function readPreferredRatio(): MediaRatio | null {
  try {
    const stored = localStorage.getItem(RATIO_PREF_KEY)
    if (stored && PRESETS.some(r => r.key === stored)) return stored as MediaRatio
  } catch { /* localStorage unavailable */ }
  return null
}

function writePreferredRatio(key: string) {
  // Never persist the dynamic custom ratio — it's host-specific.
  if (!PRESETS.some(r => r.key === key)) return
  try { localStorage.setItem(RATIO_PREF_KEY, key) } catch { /* ignore */ }
}

const RATIO_EPS = 0.01

/** Normalise the `defaultRatio` prop to a numeric ratio (or null = maintain,
 *  or undefined = "no explicit request, fall back to preference"). */
function normaliseDefault(r: MediaRatio | number | null | undefined): number | null | undefined {
  if (r === undefined) return undefined
  if (r === null) return null
  if (typeof r === 'number') return r
  return r === 'maintain' || r === 'free' ? null : r === '1:1' ? 1 : r === '9:16' ? 9 / 16 : 16 / 9
}

/** Find the preset whose ratio matches `v` (within epsilon), if any. */
function matchPreset(v: number | null): string | null {
  if (v === null) return 'maintain'
  for (const p of PRESETS) {
    if (p.value != null && Math.abs(p.value - v) < RATIO_EPS) return p.key
  }
  return null
}

function formatRatio(v: number): string {
  return `${v.toFixed(2)} : 1`
}

const HANDLE_DIR: Record<Exclude<Handle, 'body'>, { xDir: -1 | 0 | 1; yDir: -1 | 0 | 1 }> = {
  n:  { xDir:  0, yDir: -1 },
  s:  { xDir:  0, yDir:  1 },
  e:  { xDir:  1, yDir:  0 },
  w:  { xDir: -1, yDir:  0 },
  ne: { xDir:  1, yDir: -1 },
  nw: { xDir: -1, yDir: -1 },
  se: { xDir:  1, yDir:  1 },
  sw: { xDir: -1, yDir:  1 },
}

const HANDLE_CLASS: Record<Exclude<Handle, 'body'>, string> = {
  nw: 'left-[-6px] top-[-6px]    cursor-nw-resize',
  n:  'left-1/2 top-[-6px] -translate-x-1/2 cursor-n-resize',
  ne: 'right-[-6px] top-[-6px]   cursor-ne-resize',
  e:  'right-[-6px] top-1/2 -translate-y-1/2 cursor-e-resize',
  se: 'right-[-6px] bottom-[-6px] cursor-se-resize',
  s:  'left-1/2 bottom-[-6px] -translate-x-1/2 cursor-s-resize',
  sw: 'left-[-6px] bottom-[-6px] cursor-sw-resize',
  w:  'left-[-6px] top-1/2 -translate-y-1/2 cursor-w-resize',
}

const ZOOM_STEP   = 0.25
const ZOOM_MIN    = 0.25
const ZOOM_MAX    = 4

function clamp(v: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, v)) }

/** Compute where the image sits inside the container at `zoom`. */
function computeImgRect(cw: number, ch: number, nw: number, nh: number, zoom: number) {
  if (cw <= 0 || ch <= 0 || nw <= 0 || nh <= 0) return null
  // Leave room for the drag handles even when the crop reaches an image edge.
  const availableW = Math.max(1, cw - 40), availableH = Math.max(1, ch - 40)
  const cr = availableW / availableH, nr = nw / nh
  const baseDw = cr > nr ? availableH * nr : availableW
  const baseDh = cr > nr ? availableH : availableW / nr
  const dw = baseDw * zoom
  const dh = baseDh * zoom
  return { ox: (cw - dw) / 2, oy: (ch - dh) / 2, dw, dh, scale: dw / nw }
}

function fitRectForRatio(natural: { w: number; h: number }, ratio: number | null): Rect {
  if (ratio == null) {
    return { x: 0, y: 0, w: natural.w, h: natural.h }
  }
  let cw = natural.w
  let ch = natural.h
  const targetW = ch * ratio
  if (targetW <= cw) { cw = targetW }
  else               { ch = cw / ratio }
  return { x: (natural.w - cw) / 2, y: (natural.h - ch) / 2, w: cw, h: ch }
}

/** Imperative handle exposed by MediaCropField: pull the cropped File (plus the
 *  numeric ratio used) on demand. Returns null when no file / crop is ready. */
export interface MediaCropFieldHandle {
  getCropped: () => Promise<{ file: File; ratio: number | null } | null>
}

interface MediaCropFieldProps {
  file: File | null
  /** See MediaCropModal.defaultRatio. */
  defaultRatio?: MediaRatio | number | null
  /** Crop viewport height in px. Defaults to 420 (the standalone modal size);
   *  the embedded upload modal passes a smaller value to fit its column. */
  height?: number
  className?: string
}

// The bare crop surface — ratio chips + draggable/resizable crop box + zoom
// row — with no Dialog chrome. Used directly inside the media-upload modal and
// wrapped by MediaCropModal for standalone use. Cropping is pulled imperatively
// via the ref so the host owns the confirm button / upload flow.
export const MediaCropField = forwardRef<MediaCropFieldHandle, MediaCropFieldProps>(function MediaCropField(
  { file, defaultRatio, height = 420, className }, ref,
) {
  const { t } = useTranslation()
  const [src, setSrc]                 = useState<string | null>(null)
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null)
  const [crop, setCrop]               = useState<Rect | null>(null)
  const [zoom, setZoom]               = useState(1)
  // Two-finger pinch on the crop area zooms like the +/- buttons. While a
  // pinch is live the box ignores drags, so the first finger doesn't drag it.
  const pinchRef = useRef<{ d: number; z: number } | null>(null)
  const touchDist = (e: React.TouchEvent) => {
    const [a, b] = [e.touches[0], e.touches[1]]
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
  }

  // Build the chip list for this open: presets, plus a leading "Custom" chip
  // when the host requested a non-preset numeric ratio.
  const requested = normaliseDefault(defaultRatio)
  const customRatio = (typeof requested === 'number' && matchPreset(requested) === null)
    ? requested
    : null
  const options: RatioOption[] = customRatio != null
    ? [{
        key: 'custom', labelKey: 'mediaCrop.custom',
        fallback: `Custom · ${formatRatio(customRatio)}`,
        values: { ratio: formatRatio(customRatio) },
        value: customRatio,
      }, ...PRESETS]
    : PRESETS

  // The initial chip: custom if present; else the matching preset for an
  // explicit request; else the persisted preference; else 16:9.
  const computeInitialKey = useCallback((): string => {
    if (defaultRatio === 'free') return 'free'
    if (customRatio != null) return 'custom'
    if (requested === null) return 'maintain'
    if (typeof requested === 'number') return matchPreset(requested) ?? '16:9'
    return readPreferredRatio() ?? '16:9'
  }, [customRatio, requested, defaultRatio])

  const [ratioKey, setRatioKey] = useState<string>(computeInitialKey)
  const ratioValue = options.find(o => o.key === ratioKey)?.value ?? null

  // Persist whatever preset the operator settles on so the next upload starts
  // there. Custom ratios are never persisted (host-specific).
  useEffect(() => {
    if (!file) return
    writePreferredRatio(ratioKey)
  }, [file, ratioKey])

  const roRef = useRef<ResizeObserver | null>(null)
  const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 })

  // Measure the crop container via a callback ref so we capture its size the
  // instant the node mounts — independent of when `src` lands or where the
  // dialog is in its open/zoom animation.
  const setContainerNode = useCallback((node: HTMLDivElement | null) => {
    roRef.current?.disconnect()
    roRef.current = null
    if (!node) return
    const measure = () => {
      if (node.clientWidth > 0 && node.clientHeight > 0) setContainerSize({ w: node.clientWidth, h: node.clientHeight })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(node)
    roRef.current = ro
  }, [])

  useEffect(() => {
    setNaturalSize(null)
    setCrop(null)
    if (!file) { setSrc(null); return }
    const url = URL.createObjectURL(file)
    setSrc(url)
    const img = new Image()
    img.onload = () => {
      const nw = img.naturalWidth, nh = img.naturalHeight
      setNaturalSize({ w: nw, h: nh })
      const key = computeInitialKey()
      setRatioKey(key)
      setZoom(1)
      const val = options.find(o => o.key === key)?.value ?? null
      setCrop(fitRectForRatio({ w: nw, h: nh }, val))
    }
    img.src = url
    return () => { img.onload = null; URL.revokeObjectURL(url) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])

  // Re-fit the crop rect whenever the operator picks a new ratio. Keeps the
  // box centred on its previous middle so the chosen region stays roughly the
  // same area of the image.
  useEffect(() => {
    if (!naturalSize) return
    const r = ratioValue
    setCrop(prev => {
      if (!prev) return prev
      if (r == null) {
        return { x: 0, y: 0, w: naturalSize.w, h: naturalSize.h }
      }
      let cw = prev.w, ch = prev.h
      if (cw / ch > r) cw = ch * r
      else             ch = cw / r
      const cx = clamp(prev.x + (prev.w - cw) / 2, 0, naturalSize.w - cw)
      const cy = clamp(prev.y + (prev.h - ch) / 2, 0, naturalSize.h - ch)
      return { x: cx, y: cy, w: cw, h: ch }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratioKey])

  const imgRect = naturalSize ? computeImgRect(containerSize.w, containerSize.h, naturalSize.w, naturalSize.h, zoom) : null

  // Keep the crop box reachable when zoomed in. `computeImgRect` centres the
  // (possibly oversized) image in the container; at zoom > 1 the box and its
  // resize handles can land outside the overflow-hidden viewport, stranding the
  // user ("stuck"). We derive a pan that slides the image so the crop box stays
  // inside the container — purely derived (no state), and 0 at zoom 1 with a
  // fitted box, so the default behaviour is unchanged. Dragging the box toward
  // an edge then pans the image to follow it.
  let panX = 0, panY = 0
  if (imgRect && crop) {
    const m = 10
    const cw = containerSize.w, ch = containerSize.h
    const bx = imgRect.ox + crop.x * imgRect.scale
    const by = imgRect.oy + crop.y * imgRect.scale
    const bw = crop.w * imgRect.scale
    const bh = crop.h * imgRect.scale
    if (bw <= cw - m * 2) {
      if (bx < m) panX = m - bx
      else if (bx + bw > cw - m) panX = (cw - m) - (bx + bw)
    } else { panX = (cw - bw) / 2 - bx }
    if (bh <= ch - m * 2) {
      if (by < m) panY = m - by
      else if (by + bh > ch - m) panY = (ch - m) - (by + bh)
    } else { panY = (ch - bh) / 2 - by }
  }
  const ox = imgRect ? imgRect.ox + panX : 0
  const oy = imgRect ? imgRect.oy + panY : 0

  const startDrag = (handle: Handle) => (e: React.PointerEvent) => {
    if (!crop || !naturalSize || !imgRect) return
    e.preventDefault()
    e.stopPropagation()
    const startCrop = { ...crop }
    const startX = e.clientX, startY = e.clientY
    const scale = imgRect.scale
    const r = ratioValue
    const target = e.currentTarget as HTMLElement
    target.setPointerCapture?.(e.pointerId)
    const move = (ev: PointerEvent) => {
      if (pinchRef.current) return
      const dx = (ev.clientX - startX) / scale
      const dy = (ev.clientY - startY) / scale
      if (handle === 'body') {
        setCrop({
          x: clamp(startCrop.x + dx, 0, naturalSize.w - startCrop.w),
          y: clamp(startCrop.y + dy, 0, naturalSize.h - startCrop.h),
          w: startCrop.w,
          h: startCrop.h,
        })
      } else {
        setCrop(applyResize(handle, startCrop, dx, dy, r, naturalSize))
      }
    }
    const up = () => {
      target.releasePointerCapture?.(e.pointerId)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }

  // Expose the crop result imperatively so the host owns its own confirm
  // button (the standalone modal below, or the media-upload modal). Persist
  // whatever preset was settled on so the next upload starts there.
  useImperativeHandle(ref, () => ({
    getCropped: async () => {
      if (!file || !crop || !src) return null
      const cropped = await getCroppedImg(
        src,
        { x: crop.x, y: crop.y, width: crop.w, height: crop.h },
        file.name,
      )
      return { file: cropped, ratio: ratioValue }
    },
  }), [file, crop, src, ratioValue])

  const selectedLabel = ratioKey === 'custom' && customRatio != null
    ? formatRatio(customRatio)
    : ratioKey

  return (
    <div className={className}>
      {/* Ratio picker — every option's button shows its ratio label so the
          operator can pick the right one for the target. The selected ratio
          drives both the crop-box constraint and the chip below. */}
      <div className="flex gap-2 pb-3 overflow-x-auto no-scrollbar sm:flex-wrap">
        {options.map(r => (
          <button
            key={r.key}
            type="button"
            onClick={() => setRatioKey(r.key)}
            className={`shrink-0 min-h-9 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${
              ratioKey === r.key
                ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
            }`}
          >
            {t(r.labelKey, { defaultValue: r.fallback, ...r.values })}
          </button>
        ))}
      </div>

      {/* Crop area — image fills the container at the current zoom, with a
          draggable + resizable crop box overlaid. The body of the box is
          itself draggable; the eight handles resize it (ratio-constrained). */}
      <div
        ref={setContainerNode}
        className="relative w-full bg-zinc-100 select-none touch-none overflow-hidden rounded-lg border border-border/40"
        style={{ height }}
        onTouchStart={e => { if (e.touches.length === 2) pinchRef.current = { d: touchDist(e), z: zoom } }}
        onTouchMove={e => {
          const p = pinchRef.current
          if (p && e.touches.length === 2 && p.d > 0) {
            setZoom(clamp(p.z * (touchDist(e) / p.d), ZOOM_MIN, ZOOM_MAX))
          }
        }}
        onTouchEnd={e => { if (e.touches.length < 2) pinchRef.current = null }}
      >
        {src && imgRect && (
          <img
            src={src}
            alt=""
            draggable={false}
            className="absolute pointer-events-none"
            style={{ left: ox, top: oy, width: imgRect.dw, height: imgRect.dh }}
          />
        )}

        {src && imgRect && crop && (
          <div
            onPointerDown={startDrag('body')}
            className="absolute cursor-move border-2 border-white"
            style={{
              left:   ox + crop.x * imgRect.scale,
              top:    oy + crop.y * imgRect.scale,
              width:  crop.w * imgRect.scale,
              height: crop.h * imgRect.scale,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
            }}
          >
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute left-1/3 top-0 bottom-0 border-l border-white/40" />
              <div className="absolute left-2/3 top-0 bottom-0 border-l border-white/40" />
              <div className="absolute top-1/3 left-0 right-0 border-t border-white/40" />
              <div className="absolute top-2/3 left-0 right-0 border-t border-white/40" />
            </div>
            {(Object.keys(HANDLE_DIR) as Exclude<Handle, 'body'>[]).map(h => (
              <div
                key={h}
                onPointerDown={startDrag(h)}
                // 12px to see, ~36px to hit: the ::before pad is part of the
                // element, so a fingertip near the corner still grabs it.
                className={`absolute w-3 h-3 bg-white border border-zinc-400 rounded-sm shadow before:absolute before:-inset-3 before:content-[''] ${HANDLE_CLASS[h]}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Zoom row — buttons cap the zoom range so users can't escape the
          preview frame; the readout shows the active crop size + ratio. */}
      <div className="pt-3 flex items-center justify-between text-xs">
        <div className="flex items-center gap-1">
          <Button
            size="sm" variant="outline" className="h-10 w-10 sm:h-8 sm:w-8 p-0"
            onClick={() => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
            disabled={zoom <= ZOOM_MIN}
            aria-label={t('common.zoomOut', 'Zoom out')}
          >
            <ZoomOut className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm" variant="outline" className="h-10 w-10 sm:h-8 sm:w-8 p-0"
            onClick={() => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
            disabled={zoom >= ZOOM_MAX}
            aria-label={t('common.zoomIn', 'Zoom in')}
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm" variant="ghost" className="h-10 sm:h-8 px-2 gap-1.5"
            onClick={() => { setZoom(1); if (naturalSize) setCrop(fitRectForRatio(naturalSize, ratioValue)) }}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span className="tabular-nums">{t('mediaCrop.reset', 'Reset crop')} · {Math.round(zoom * 100)}%</span>
          </Button>
        </div>
        <div className="flex items-center gap-3 text-muted-foreground">
          <span>{crop ? `${Math.round(crop.w)} × ${Math.round(crop.h)} px` : '—'}</span>
          <span className="font-mono">{selectedLabel}</span>
        </div>
      </div>
    </div>
  )
})

interface MediaCropModalProps {
  file: File | null
  /** Default ratio when the modal opens. Accepts a preset key (`'16:9'` …),
   *  a raw numeric ratio (e.g. `16 / 13.5`), or `null` for "maintain". When
   *  omitted we fall back to the operator's last-used preset, then 16:9. A
   *  non-preset number is offered as a locked "Custom" chip. */
  defaultRatio?: MediaRatio | number | null
  /** Called with the cropped File. The second arg is the numeric ratio used
   *  (null = maintain) — most callers can ignore it. */
  onConfirm: (cropped: File, ratio: number | null) => void | Promise<void>
  onCancel: () => void
}

// Standalone crop dialog — a thin Dialog shell around MediaCropField. Kept for
// the many call sites (products, brand assets, logo pickers, …) that crop an
// image on its own, outside a larger upload modal.
export function MediaCropModal({ file, defaultRatio, onConfirm, onCancel }: MediaCropModalProps) {
  const { t } = useTranslation()
  const fieldRef = useRef<MediaCropFieldHandle>(null)
  const [confirming, setConfirming] = useState(false)
  const attempt = useRef<AbortController | null>(null)
  const activeFile = useRef<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Keep the active file visible while its parent uploads it, even when the
  // parent clears its staged file early. X must remain available to abort.
  useEffect(() => { if (file) setError(null) }, [file])
  useEffect(() => () => { attempt.current?.abort() }, [])

  const cancel = () => {
    attempt.current?.abort()
    attempt.current = null
    activeFile.current = null
    setConfirming(false)
    setError(null)
    onCancel()
  }

  const handleConfirm = async () => {
    if (attempt.current || !file) return
    const controller = new AbortController()
    attempt.current = controller
    activeFile.current = file
    setConfirming(true)
    setError(null)
    try {
      const result = await fieldRef.current?.getCropped()
      if (controller.signal.aborted) return
      if (!result) throw new Error('The image is not ready. Please try again.')
      bindUploadSignal(result.file, controller.signal)
      await onConfirm(result.file, result.ratio)
    } catch (err) {
      if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Could not upload the image. Please try again.')
    } finally {
      if (attempt.current === controller) {
        attempt.current = null
        activeFile.current = null
        setConfirming(false)
      }
    }
  }

  return (
    <Dialog open={!!file || confirming} onOpenChange={o => { if (!o) cancel() }}>
      <DialogContent className="max-w-3xl w-[95vw] p-0 gap-0 overflow-hidden flex flex-col max-h-[92vh]">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>{t('mediaCrop.title', 'Crop image')}</DialogTitle>
            {/* Recommended aspect ratios + sizes for horizontal / vertical /
                video, surfaced as two ⓘ badges so operators know what to prep. */}
            <AssetSpecsHint />
          </div>
        </DialogHeader>

        <div className={`min-h-0 overflow-y-auto ${confirming ? 'pointer-events-none opacity-70' : ''}`} aria-busy={confirming}>
          <MediaCropField ref={fieldRef} file={confirming ? activeFile.current : file} defaultRatio={defaultRatio} className="px-5 py-3" />
        </div>
        {error && <p role="alert" className="px-5 py-2 text-sm text-destructive">{error}</p>}

        <DialogFooter className="px-5 py-3 gap-2 border-t border-border">
          <Button variant="outline" size="sm" onClick={cancel}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button size="sm" onClick={handleConfirm} disabled={confirming || !file} className="gap-2 min-w-[90px]">
            {confirming && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {t('common.upload', 'Upload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default MediaCropModal
